interface SpringPaginationProps {
    currentPage: number;
    totalPages: number;
    totalElements: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
}

/**
 * Bara de paginare a rapoartelor.
 *
 * Se randeaza **doar de la `md` in sus** (`ReportPageLayout` o pune sub
 * `hidden md:block`): sub `md` raportul e rezumat, fara tabel, deci nu are ce
 * pagina.
 *
 * Faza 3.0 (2026-09-03): butoanele si selectul erau de **28×26px**, respectiv
 * **34px** inaltime — sub pragul de 44px masurat la 1280px in inventarul B.
 * Acum toate controalele au 44px pe ambele axe. Restul e neschimbat: aceleasi
 * simboluri, aceleasi `aria-label`-uri, acelasi comportament de dezactivare.
 */
export function SpringPagination({
    currentPage,
    totalPages,
    totalElements,
    pageSize,
    onPageChange,
    onPageSizeChange
}: SpringPaginationProps) {
    const pageSizeOptions = [10, 20, 50, 100];

    // Calculate displayed range
    const startElement = currentPage * pageSize + 1;
    const endElement = Math.min((currentPage + 1) * pageSize, totalElements);

    // Selectul ramane la 16px (`text-base`), nu la 15,2px ca eticheta de langa
    // el: masurat, `text-small` il ducea sub pragul de 16px si aparea in
    // numaratoarea „inputuri sub 16px" — exact regresia pe care o repara acest
    // PR in `TableDateRangeFilter`.
    //
    // Un singur loc pentru reteta butonului: 44×44, contur de control, fara
    // salt de layout la dezactivare (opacitate, nu disparitie).
    const buton =
        // Bordura e SINGURUL indiciu ca aici se apasa (glifele « » n-au fundal),
        // deci intra sub 1.4.11: prag 3:1. `border-gray-300` (#d1d5db) pe alb da
        // 1,47 — masurat pe toate cele cinci controale, la 360 si la 1280.
        'inline-flex size-11 shrink-0 items-center justify-center rounded-md border border-muted-foreground ' +
        'text-small text-dark transition-colors hover:bg-light ' +
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-magenta ' +
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent';

    /* 🔴 Randul nu se impacheta la nicio latime. Masurat la 360px pe /mesaje cu
       10 mesaje: sagetile „Pagina urmatoare" si „Ultima pagina" ajungeau la
       451..487 si 495..531, iar `scrollWidth == clientWidth == 360` — adica erau
       taiate de `overflow:hidden` din shell, fara scroll lateral prin care sa fie
       atinse. Pagina 2 era inaccesibila pe telefon.
       Sub `sm` totul merge pe coloana; de la `sm` in sus, randarea e neschimbata.
       `shrink-0` pe butoane: fara el, `size-11` era comprimat la 36x44. */
    return (
        <div className="ecotic-report-rail flex flex-col items-stretch gap-3 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            {/* Results info */}
            <div className="text-small text-gray-700">
                Afișare{' '}
                <span className="font-medium tabular-nums">{startElement}</span>
                {' - '}
                <span className="font-medium tabular-nums">{endElement}</span>
                {' din '}
                <span className="font-medium tabular-nums">{totalElements}</span>
                {' rezultate'}
            </div>

            {/* Page controls */}
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
                {/* Page size selector */}
                <div className="flex items-center gap-2">
                    <label htmlFor="pagination-size" className="text-small text-gray-700">
                        Elemente pe pagină:
                    </label>
                    <select
                        id="pagination-size"
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        className="h-11 rounded-md border border-muted-foreground bg-white px-2 text-base text-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-magenta"
                    >
                        {pageSizeOptions.map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>
                </div>

                {/* Page navigation */}
                <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                        onClick={() => onPageChange(0)}
                        disabled={currentPage === 0}
                        className={buton}
                        aria-label="Prima pagină"
                    >
                        ««
                    </button>
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                        className={buton}
                        aria-label="Pagina anterioară"
                    >
                        «
                    </button>
                    <span className="px-3 text-small text-dark tabular-nums">
                        Pagina {currentPage + 1} din {totalPages}
                    </span>
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1}
                        className={buton}
                        aria-label="Pagina următoare"
                    >
                        »
                    </button>
                    <button
                        onClick={() => onPageChange(totalPages - 1)}
                        disabled={currentPage >= totalPages - 1}
                        className={buton}
                        aria-label="Ultima pagină"
                    >
                        »»
                    </button>
                </div>
            </div>
        </div>
    );
}
